import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Divider,
  Alert,
} from '@mui/material'
import { makeStyles } from '../../utils/hooks/makeStyles'

// ─────────────────────────────────────────────
// Styles (makeStyles pattern used in this project)
// ─────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  paper: {
    padding: '24px',
    backgroundColor: '#ffffff',
  },
  screeningTitle: {
    color: '#4682b4',
    fontWeight: 700,
    fontSize: '18px',
    marginBottom: '4px',
  },
  mainTitle: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  stopText: {
    color: '#cc0000',
    fontWeight: 700,
  },
  subTitle: {
    color: '#4682b4',
    fontSize: '13px',
    marginBottom: '4px',
    cursor: 'default',
  },
  description: {
    fontSize: '13px',
    marginBottom: '16px',
    color: '#333333',
  },
  questionRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: '20px',
    gap: '16px',
  },
  radioGroupHorizontal: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    minWidth: '90px',
  },
  questionContent: {
    flex: 1,
  },
  questionLetter: {
    fontSize: '28px',
    fontWeight: 900,
    lineHeight: 1,
  },
  questionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    display: 'inline',
  },
  questionBody: {
    fontSize: '13px',
    color: '#333333',
    marginTop: '4px',
  },
  boldText: {
    fontWeight: 700,
  },
  bmiSection: {
    marginTop: '12px',
    marginLeft: '4px',
  },
  bmiTitle: {
    fontSize: '13px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  bmiInputRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '8px',
  },
  bmiInputLabel: {
    fontSize: '13px',
    minWidth: '50px',
  },
  bmiInput: {
    width: '100px',
  },
  bmiResultRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  bmiResultLabel: {
    fontSize: '13px',
  },
  bmiResultBox: {
    backgroundColor: '#d0d0d0',
    padding: '4px 16px',
    borderRadius: '4px',
    fontSize: '13px',
    minWidth: '80px',
    textAlign: 'center',
  },
  seeResultBtn: {
    textTransform: 'capitalize',
    backgroundColor: '#ffffff',
    color: '#000000',
    border: '1px solid #999999',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    fontSize: '13px',
    padding: '4px 16px',
    marginTop: '8px',
  },
  resultBox: {
    backgroundColor: '#dce8f5',
    padding: '16px',
    borderRadius: '4px',
    marginTop: '16px',
  },
  resultBoxTitle: {
    fontWeight: 700,
    fontSize: '13px',
    marginBottom: '8px',
  },
  resultBoxLow: {
    color: '#cc6600',
    fontSize: '13px',
    marginBottom: '2px',
  },
  resultBoxIntermediate: {
    color: '#cc6600',
    fontSize: '13px',
    marginBottom: '2px',
  },
  resultBoxHigh: {
    color: '#cc6600',
    fontSize: '13px',
    marginBottom: '2px',
  },
  resultBoxNote: {
    fontSize: '13px',
    color: '#333333',
    marginBottom: '2px',
  },
  // resultBoxProperty: {
  //   color: '#cc0000',
  //   fontWeight: 700,
  //   fontSize: '13px',
  //   marginTop: '8px',
  // },
  resultAlertHigh: {
    marginTop: '12px',
    backgroundColor: '#ffdddd',
    borderLeft: '4px solid #cc0000',
  },
  resultAlertIntermediate: {
    marginTop: '12px',
    backgroundColor: '#fff3cd',
    borderLeft: '4px solid #cc6600',
  },
  resultAlertLow: {
    marginTop: '12px',
    backgroundColor: '#ddffdd',
    borderLeft: '4px solid #008800',
  },
  radioLabel: {
    fontSize: '12px',
    '& .MuiFormControlLabel-label': {
      fontSize: '12px',
    },
    '& .MuiRadio-root': {
      padding: '4px',
    },
  },
  divider: {
    margin: '8px 0 16px 0',
  },
  formHelperError: {
    color: '#d32f2f',
    fontSize: '11px',
  },
  submitBtn: {
    textTransform: 'capitalize',
    backgroundColor: '#4682b4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#336799',
    },
    fontSize: '13px',
    padding: '6px 24px',
  },
  actionsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
  },
}))

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
export interface StopBangFormValues {
  snoring: string
  tired: string
  observed: string
  pressure: string
  bmiAbove35: string
  ageAbove50: string
  neckLarge: string
  genderMale: string
  resultCount: number | null
  result: string | null
}

export interface StopBangResult {
  score: number
  riskLevel: 'Low Risk' | 'Intermediate Risk' | 'High Risk'
  criteria: string[]
}

// ─────────────────────────────────────────────
// Validation schema
// ─────────────────────────────────────────────
const validationSchema = Yup.object({
  snoring: Yup.string().required('Please select Yes or No'),
  tired: Yup.string().required('Please select Yes or No'),
  observed: Yup.string().required('Please select Yes or No'),
  pressure: Yup.string().required('Please select Yes or No'),
  bmiAbove35: Yup.string().required('Please select Yes or No'),
  ageAbove50: Yup.string().required('Please select Yes or No'),
  neckLarge: Yup.string().required('Please select Yes or No'),
  genderMale: Yup.string().required('Please select Yes or No'),
})

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const calculateResult = (values: StopBangFormValues): StopBangResult => {
  const yesCount = [
    values.snoring,
    values.tired,
    values.observed,
    values.pressure,
    values.bmiAbove35,
    values.ageAbove50,
    values.neckLarge,
    values.genderMale,
  ].filter((v) => v === 'yes').length

  const stopYesCount = [
    values.snoring,
    values.tired,
    values.observed,
    values.pressure,
  ].filter((v) => v === 'yes').length

  const criteria: string[] = []
  let riskLevel: StopBangResult['riskLevel'] = 'Low Risk'

  // High Risk sub-criteria flags
  const isHighByScore = yesCount >= 5
  const isHighByMale = stopYesCount >= 2 && values.genderMale === 'yes'
  const isHighByBMI = stopYesCount >= 2 && values.bmiAbove35 === 'yes'
  const isHighByNeck = stopYesCount >= 2 && values.neckLarge === 'yes'

  if (isHighByScore || isHighByMale || isHighByBMI || isHighByNeck) {
    riskLevel = 'High Risk'
    if (isHighByScore) criteria.push(`Yes to ${yesCount} of 8 questions`)
    if (isHighByMale)
      criteria.push('Yes to 2 or more of 4 STOP questions + male gender')
    if (isHighByBMI)
      criteria.push('Yes to 2 or more of 4 STOP questions + BMI > 35 kg/m²')
    if (isHighByNeck)
      criteria.push(
        'Yes to 2 or more of 4 STOP questions + neck circumference 16 inches / 40cm'
      )
  } else if (yesCount >= 3 && yesCount <= 4) {
    riskLevel = 'Intermediate Risk'
    criteria.push(`Yes to ${yesCount} of 8 questions (3-4)`)
  } else {
    riskLevel = 'Low Risk'
    criteria.push(`Yes to ${yesCount} of 8 questions (0-2)`)
  }

  return { score: yesCount, riskLevel, criteria }
}

// ─────────────────────────────────────────────
// Default initial values
// ─────────────────────────────────────────────
const defaultInitialValues: StopBangFormValues = {
  snoring: '',
  tired: '',
  observed: '',
  pressure: '',
  bmiAbove35: '',
  ageAbove50: '',
  neckLarge: '',
  genderMale: '',
  resultCount: null,
  result: null,
}

// ─────────────────────────────────────────────
// Props & ref handle
// ─────────────────────────────────────────────
export interface SleepAnalysisHandle {
  /** Triggers validation; resolves true if valid */
  validate: () => Promise<boolean>
  /** Returns the current form values */
  getValues: () => StopBangFormValues
}

interface SleepAnalysisProps {
  /** Pre-fill the form from a previously saved JSON */
  prefillData?: Partial<StopBangFormValues>
  /** height in cm from the parent vitals form */
  height?: string
  /** weight in kg from the parent vitals form */
  weight?: string
  /** BMI computed by the parent vitals form */
  bmi?: string
  /** Patient age in years (e.g. 50) — auto-derives ageAbove50 */
  age?: number | string
  /** Patient gender: 'male' | 'female' — auto-derives genderMale */
  gender?: string
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const SleepAnalysis = forwardRef<SleepAnalysisHandle, SleepAnalysisProps>(
  ({ prefillData, height = '', weight = '', bmi = '', age, gender }, ref) => {
    const classes = useStyles()
    const [result, setResult] = useState<StopBangResult | null>(null)

    const formik = useFormik<StopBangFormValues>({
      initialValues: {
        ...defaultInitialValues,
        // Only spread prefillData values that are non-null and non-empty
        ...(prefillData
          ? Object.fromEntries(
              Object.entries(prefillData).filter(
                ([, v]) => v !== null && v !== undefined && v !== ''
              )
            )
          : {}),
      },
      validationSchema,
      onSubmit: () => {
        // Submission is driven by the parent CampPatientVitals form
      },
      enableReinitialize: true,
    })

    // Auto-derive bmiAbove35 from the parent-computed BMI value (height cm, weight kg)
    React.useEffect(() => {
      if (bmi !== '' && Number(bmi) > 0) {
        const bmiVal = parseFloat(bmi)
        formik.setFieldValue(
          'bmiAbove35',
          !isNaN(bmiVal) && bmiVal > 35 ? 'yes' : 'no'
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bmi])

    // Auto-derive ageAbove50 from patient age (age > 50 means yes)
    React.useEffect(() => {
      if (age !== undefined && age !== '') {
        const ageVal = Number(age)
        formik.setFieldValue(
          'ageAbove50',
          !isNaN(ageVal) && ageVal > 50 ? 'yes' : 'no'
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [age])

    // Auto-derive genderMale from patient gender ('male' → yes, anything else → no)
    React.useEffect(() => {
      if (gender !== undefined && gender !== '') {
        formik.setFieldValue(
          'genderMale',
          gender.toLowerCase() === 'male' ? 'yes' : 'no'
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gender])

    // Auto-calculate resultCount and result once all 8 questions are answered
    React.useEffect(() => {
      const {
        snoring,
        tired,
        observed,
        pressure,
        bmiAbove35,
        ageAbove50,
        neckLarge,
        genderMale,
      } = formik.values
      const allAnswered = [
        snoring,
        tired,
        observed,
        pressure,
        bmiAbove35,
        ageAbove50,
        neckLarge,
        genderMale,
      ].every((v) => v === 'yes' || v === 'no')

      if (allAnswered) {
        const res = calculateResult(formik.values)
        formik.setFieldValue('resultCount', res.score)
        formik.setFieldValue('result', res.riskLevel)
        setResult(res)
      } else {
        formik.setFieldValue('resultCount', null)
        formik.setFieldValue('result', null)
        setResult(null)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      formik.values.snoring,
      formik.values.tired,
      formik.values.observed,
      formik.values.pressure,
      formik.values.bmiAbove35,
      formik.values.ageAbove50,
      formik.values.neckLarge,
      formik.values.genderMale,
    ])

    // Expose validate + getValues to the parent via ref
    useImperativeHandle(ref, () => ({
      validate: async () => {
        const errors = await formik.validateForm()
        formik.setTouched(
          Object.keys(errors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
        )
        return Object.keys(errors).length === 0
      },
      getValues: () => formik.values,
    }))

    // const handleSeeResult = () => {
    //   if (formik.values.resultCount !== null) {
    //     const res = calculateResult(formik.values)
    //     setResult(res)
    //   }
    // }

    const renderYesNoRadio = (
      fieldName: keyof StopBangFormValues,
      label?: string
    ) => (
      <FormControl
        component='fieldset'
        error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
      >
        {label && (
          <FormLabel component='legend' className={classes?.radioLabel}>
            {label}
          </FormLabel>
        )}
        <RadioGroup
          row
          name={fieldName}
          value={formik.values[fieldName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={classes?.radioGroupHorizontal}
        >
          <FormControlLabel
            value='yes'
            control={<Radio size='small' />}
            label='Yes'
            className={classes?.radioLabel}
          />
          <FormControlLabel
            value='no'
            control={<Radio size='small' />}
            label='No'
            className={classes?.radioLabel}
          />
        </RadioGroup>
        {formik.touched[fieldName] && formik.errors[fieldName] && (
          <FormHelperText className={classes?.formHelperError}>
            {formik.errors[fieldName] as string}
          </FormHelperText>
        )}
      </FormControl>
    )

    return (
      <Box className={classes?.root}>
        {/* Header */}
        {/* <Typography className={classes?.screeningTitle}>Screening</Typography> */}
        <Typography className={classes?.mainTitle}>
          <span className={classes?.stopText}>STOP</span>-Bang Questionnaire
        </Typography>
        {/* <Typography className={classes?.subTitle}>
          Is it possible that you have ...
        </Typography>
        <Typography className={classes?.subTitle}>
          Obstructive Sleep Apnea (OSA)?
        </Typography> */}
        <Typography className={classes?.description}>
          Please answer the following questions below to determine if you might
          be at risk.
        </Typography>

        <Divider className={classes?.divider} />

        {/* S – Snoring */}
        <Box className={classes?.questionRow}>
          {renderYesNoRadio('snoring')}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              S
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              noring ?
            </Typography>
            <Typography className={classes?.questionBody}>
              Do you <span className={classes?.boldText}>Snore Loudly</span>{' '}
              (loud enough to be heard through closed doors or your bed-partner
              elbows you for snoring at night)?
            </Typography>
          </Box>
        </Box>

        {/* T – Tired */}
        <Box className={classes?.questionRow}>
          {renderYesNoRadio('tired')}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              T
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              ired ?
            </Typography>
            <Typography className={classes?.questionBody}>
              Do you often feel{' '}
              <span className={classes?.boldText}>
                Tired, Fatigued, or Sleepy
              </span>{' '}
              during the daytime (such as falling asleep during driving or
              talking to someone)?
            </Typography>
          </Box>
        </Box>

        {/* O – Observed */}
        <Box className={classes?.questionRow}>
          {renderYesNoRadio('observed')}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              O
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              bserved ?
            </Typography>
            <Typography className={classes?.questionBody}>
              Has anyone <span className={classes?.boldText}>Observed</span> you{' '}
              <span className={classes?.boldText}>Stop Breathing</span> or{' '}
              <span className={classes?.boldText}>Choking/Gasping</span> during
              your sleep ?
            </Typography>
          </Box>
        </Box>

        {/* P – Pressure */}
        <Box className={classes?.questionRow}>
          {renderYesNoRadio('pressure')}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              P
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              ressure ?
            </Typography>
            <Typography className={classes?.questionBody}>
              Do you have or are being treated for{' '}
              <span className={classes?.boldText}>High Blood Pressure</span> ?
            </Typography>
          </Box>
        </Box>

        {/* B – BMI */}
        <Box className={classes?.questionRow}>
          <FormControl
            component='fieldset'
            error={
              formik.touched.bmiAbove35 && Boolean(formik.errors.bmiAbove35)
            }
          >
            <RadioGroup
              row
              name='bmiAbove35'
              value={formik.values.bmiAbove35}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={classes?.radioGroupHorizontal}
            >
              <FormControlLabel
                value='yes'
                control={<Radio size='small' />}
                label='Yes'
                className={classes?.radioLabel}
              />
              <FormControlLabel
                value='no'
                control={<Radio size='small' />}
                label='No'
                className={classes?.radioLabel}
              />
            </RadioGroup>
            {formik.touched.bmiAbove35 && formik.errors.bmiAbove35 && (
              <FormHelperText className={classes?.formHelperError}>
                {formik.errors.bmiAbove35}
              </FormHelperText>
            )}
          </FormControl>
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              B
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              ody Mass Index more than 35 kg/m²?
            </Typography>
            <Typography className={classes?.questionBody}>
              Auto-derived from height (cm) and weight (kg) recorded in
              Anthropometry.
            </Typography>

            {/* BMI Info (read-only, synced from parent vitals) */}
            <Box className={classes?.bmiSection}>
              <Box className={classes?.bmiInputRow}>
                <Typography className={classes?.bmiInputLabel}>
                  Height:
                </Typography>
                <TextField
                  size='small'
                  value={height}
                  className={classes?.bmiInput}
                  InputProps={{ readOnly: true }}
                  inputProps={{ tabIndex: -1 }}
                />
                <Typography className={classes?.bmiInputLabel}>
                  Weight:
                </Typography>
                <TextField
                  size='small'
                  value={weight}
                  className={classes?.bmiInput}
                  InputProps={{ readOnly: true }}
                  inputProps={{ tabIndex: -1 }}
                />
              </Box>

              <Box className={classes?.bmiResultRow}>
                <Typography className={classes?.bmiResultLabel}>
                  BMI:
                </Typography>
                <Box className={classes?.bmiResultBox}>{bmi || ''}</Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* A – Age */}
        <Box className={classes?.questionRow}>
          {age !== undefined && age !== '' ? (
            <FormControl
              component='fieldset'
              error={
                formik.touched.ageAbove50 && Boolean(formik.errors.ageAbove50)
              }
            >
              <RadioGroup
                row
                name='ageAbove50'
                value={formik.values.ageAbove50}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={classes?.radioGroupHorizontal}
              >
                <FormControlLabel
                  value='yes'
                  control={<Radio size='small' />}
                  label='Yes'
                  className={classes?.radioLabel}
                />
                <FormControlLabel
                  value='no'
                  control={<Radio size='small' />}
                  label='No'
                  className={classes?.radioLabel}
                />
              </RadioGroup>
              {formik.touched.ageAbove50 && formik.errors.ageAbove50 && (
                <FormHelperText className={classes?.formHelperError}>
                  {formik.errors.ageAbove50}
                </FormHelperText>
              )}
            </FormControl>
          ) : (
            renderYesNoRadio('ageAbove50')
          )}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              A
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              ge older than 50 ?
            </Typography>
            {age !== undefined && age !== '' && (
              <Typography className={classes?.questionBody}>
                Auto-derived from patient age.
              </Typography>
            )}
          </Box>
        </Box>

        {/* N – Neck */}
        <Box className={classes?.questionRow}>
          {renderYesNoRadio('neckLarge')}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              N
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              eck size large ? (Measured around Adams apple)
            </Typography>
            <Typography className={classes?.questionBody}>
              Is your shirt collar 16 inches / 40cm or larger?
            </Typography>
          </Box>
        </Box>

        {/* G – Gender */}
        <Box className={classes?.questionRow}>
          {gender !== undefined && gender !== '' ? (
            <FormControl
              component='fieldset'
              error={
                formik.touched.genderMale && Boolean(formik.errors.genderMale)
              }
            >
              <RadioGroup
                row
                name='genderMale'
                value={formik.values.genderMale}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={classes?.radioGroupHorizontal}
              >
                <FormControlLabel
                  value='yes'
                  control={<Radio size='small' />}
                  label='Yes'
                  className={classes?.radioLabel}
                />
                <FormControlLabel
                  value='no'
                  control={<Radio size='small' />}
                  label='No'
                  className={classes?.radioLabel}
                />
              </RadioGroup>
              {formik.touched.genderMale && formik.errors.genderMale && (
                <FormHelperText className={classes?.formHelperError}>
                  {formik.errors.genderMale}
                </FormHelperText>
              )}
            </FormControl>
          ) : (
            renderYesNoRadio('genderMale')
          )}
          <Box className={classes?.questionContent}>
            <Typography component='span' className={classes?.questionLetter}>
              G
            </Typography>
            <Typography component='span' className={classes?.questionTitle}>
              ender = Male ?
            </Typography>
            {gender !== undefined && gender !== '' && (
              <Typography className={classes?.questionBody}>
                Auto-derived from patient gender.
              </Typography>
            )}
          </Box>
        </Box>

        {/* See Result */}
        {/* <Box className={classes?.actionsRow}>
          <Button
            className={classes?.seeResultBtn}
            onClick={handleSeeResult}
            type='button'
            variant='outlined'
          >
            See Result
          </Button>
        </Box> */}

        {/* Result Panel */}
        {result && (
          <Box className={classes?.resultBox}>
            <Typography className={classes?.resultBoxTitle}>
              For general population
            </Typography>
            <Typography className={classes?.resultBoxLow}>
              OSA - Low Risk : Yes to 0 - 2 questions
            </Typography>
            <Typography className={classes?.resultBoxIntermediate}>
              OSA - Intermediate Risk : Yes to 3 - 4 questions
            </Typography>
            <Typography className={classes?.resultBoxHigh}>
              OSA - High Risk : Yes to 5 - 8 questions
            </Typography>
            <Typography className={classes?.resultBoxNote}>
              or Yes to 2 or more of 4 STOP questions + male gender
            </Typography>
            <Typography className={classes?.resultBoxNote}>
              or Yes to 2 or more of 4 STOP questions + BMI &gt; 35kg/m²
            </Typography>
            <Typography className={classes?.resultBoxNote}>
              or Yes to 2 or more of 4 STOP questions + neck circumference 16
              inches / 40cm
            </Typography>
            {/* <Typography className={classes?.resultBoxProperty}>
              Property of University Health Network.
            </Typography> */}

            <Alert
              severity={
                result.riskLevel === 'High Risk'
                  ? 'error'
                  : result.riskLevel === 'Intermediate Risk'
                  ? 'warning'
                  : 'success'
              }
              className={
                result.riskLevel === 'High Risk'
                  ? classes?.resultAlertHigh
                  : result.riskLevel === 'Intermediate Risk'
                  ? classes?.resultAlertIntermediate
                  : classes?.resultAlertLow
              }
            >
              <strong>Score: {result.score} / 8</strong>
              {'  —  '}
              <strong>{result.riskLevel}</strong>
              <br />
              {result.criteria.map((c, i) => (
                <span key={i}>{c}</span>
              ))}
            </Alert>
          </Box>
        )}
      </Box>
    )
  }
)

SleepAnalysis.displayName = 'SleepAnalysis'

export default SleepAnalysis
